// UTM capture for paid-traffic attribution.
//
// On first load, parse window.location.search for the five canonical UTM
// keys and persist them to sessionStorage. They survive client-side
// navigations within the same tab; subsequent calls to createSession /
// startCheckout pick them up via getUtmParams() so they end up on the
// Stripe Checkout metadata and on the Order doc.
//
// WHY sessionStorage and not localStorage:
//   The attribution window is one browsing session. If the user returns
//   tomorrow and buys, that should attribute to whatever brought them
//   back, not yesterday's stale params. sessionStorage clears on tab close.
//
// WHY 200-char cap per value:
//   Stripe metadata has a 500-char-per-value limit. Capping at 200 leaves
//   headroom and prevents pathological URLs from ballooning storage.

const STORAGE_KEY = 'crayo-utm';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const MAX_VALUE_LEN = 200;

function captureFromUrl() {
  if (typeof window === 'undefined') return;
  let params;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }
  const captured = {};
  for (const key of UTM_KEYS) {
    const v = params.get(key);
    if (v && typeof v === 'string') {
      captured[key] = v.slice(0, MAX_VALUE_LEN);
    }
  }
  if (Object.keys(captured).length === 0) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
  } catch {
    // Quota / privacy-mode failure: UTMs simply do not persist this session.
    // We still hold them on this page load via the closure below.
  }
}

/**
 * Snapshot of the UTM parameters captured for this browsing session, or
 * null if none were present in the original landing URL.
 *
 * Returned object uses the dotted UTM key names (`utm_source` etc.) so the
 * caller can spread it directly into a request body.
 */
export function getUtmParams() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

// Side-effect: capture on first import. The first import happens in
// src/main.jsx before any component renders, so by the time a component
// calls getUtmParams() the snapshot is already in sessionStorage.
captureFromUrl();
